import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormArray, FormControl, ValidationErrors } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { NotificationsService } from '../../../../../theme/services/notifications/notifications.service';
import { calendarES } from '../../../../../theme/functions/calendarES';
import { CandidateDisabilitiesService } from '../../../services/candidate-disabilities.service';
import { ListInsurances, newTypesOfDisabilities, IsTranscribedOptions } from '../data.constants';
import { formatDate } from '../../../../../theme/functions/formatDate.function';
import { Router } from '@angular/router';
import { sortObj } from '../../../../../theme/functions/sortObj.function';
import { getDiffDateDisabilty } from '../../../../../theme/functions/getDiffDate.function';

@Component({
  selector: 'app-new-candidate-disabilitie',
  templateUrl: './new-candidate-disabilitie.component.html',
  styleUrls: ['./new-candidate-disabilitie.component.scss']
})
export class NewCandidateDisabilitieComponent implements OnInit {

  public form: FormGroup;
  public typesOfDisabilities = newTypesOfDisabilities;
  public listEps: any[] = [];
  public selectedDisability: string | null = null;
  public es: any;
  public candidate: any;
  public candidateId: any;
  public candidateEmail: any;
  public candidateEps: any;
  public promiseForm: Promise<any>;
  public showModal: boolean = false;
  public showView: boolean = true;
  public modalData: { id: number; creationDate: string } | null = null;
  public previousDisabilityType: string;
  public alertMessage: string = '';
  public formGroupNameDocuments: string = 'documents';
  public isTranscribedOptions = [...IsTranscribedOptions];

  constructor(
    private fb: FormBuilder,
    public translate: TranslateService,
    private candidateDisabilitiesService: CandidateDisabilitiesService,
    private notificationsService: NotificationsService,
    private router: Router,
  ) {
    this.es = calendarES();
    this.isTranscribedOptions = this.translateLabels(this.isTranscribedOptions, 'label');
  }

  public translateLabels(array: any[], labelKey: string) {
    return array.map((item) => {
      this.translate.get(item[labelKey]).subscribe((translatedLabel: string) => {
        item[labelKey] = translatedLabel;
      });
      return item;
    });
  }

  ngOnInit(): void {
    this.listEps = sortObj( ListInsurances,'asc');
    this.loadCandidateInfo();
    this.form = this.fb.group({
      disabilityType: ['', Validators.required],
      dateStart: ['', Validators.required],
      dateEnd: ['', Validators.required],
      eps: [{
        value: this.candidateEps || '',
        disabled: this.candidateEps
      }, this.candidateEps ? [] : [Validators.required]],
      type_of_contingency: ['', Validators.required],
      arl: ['ARL01'],
      additionalInfo: ['', Validators.maxLength(500)],
      is_transcribed: ['', Validators.required],
    });
  }

  public cancel() {
    this.form.reset();
    this.router.navigateByUrl("/candidate/disabilities");
  }

  private loadCandidateInfo() {
    const profile = localStorage.getItem('profile');
    if (profile) {
      const { candidate, email, candidate_id } = JSON.parse(profile);
      this.candidate = candidate;
      this.candidateEmail = email;
      this.candidateId = candidate_id;
      this.candidateEps = candidate.eps;
    }
  }

  public saveAndExit() {
    if (this.form.valid) {
      this.createFormDataAndSave();
    } else {
      this.getFormValidationErrors(this.form);
    }
  }

  private createFormDataAndSave() {
    const formData = new FormData();
    const { disabilityType, dateStart, dateEnd, additionalInfo } = this.form.controls;

    formData.append('disabilityType', disabilityType.value);
    formData.append('candidate_id', this.candidateId);
    formData.append('dateStart', formatDate(new Date(dateStart.value)));
    formData.append('dateEnd', formatDate(new Date(dateEnd.value)));

    this.addEpsOrArl(formData);
    this.handleTypeOfContingency(formData);
    formData.append('additionalInfo', additionalInfo.value);
    formData.append('is_transcribed', this.form.get('is_transcribed').value);
    this.appendDocuments(formData, this.formGroupNameDocuments);
    this.sendDisabilityData(formData);
  }

  private addEpsOrArl(formData: FormData) {
    const epsOrArl = this.selectedDisability === 'work_accident' ? 'arl' : 'eps';
    formData.append(epsOrArl, this.form.get(epsOrArl).value);
  }

  private handleTypeOfContingency(formData: FormData) {
    const isMaternalOrParental = ['maternal_leave', 'parental_leave'].includes(this.selectedDisability);

    if (isMaternalOrParental) {
      this.form.get('type_of_contingency').setValue('start');
      formData.append('typeOfContingency', 'start');
    } else {
      formData.append('typeOfContingency', this.form.get('type_of_contingency').value);
    }
  }

  private appendDocuments(formData: FormData, disabilityType: string) {
    const documentsGroup = this.form.get(disabilityType) as FormGroup;

    if (documentsGroup) {
      Object.keys(documentsGroup.controls).forEach(key => {
        const formArray = documentsGroup.get(key) as FormArray;
        formArray.controls.forEach((file: FormControl) => {
          if (file.value) {
            formData.append(`documents[${key}][]`, file.value);
          }
        });
      });
    }
  }

  private sendDisabilityData(formData: FormData) {
    this.promiseForm = this.candidateDisabilitiesService.newCandidateDisabilitie(formData).toPromise()
      .then(response => {
        const { id, creationDate } = JSON.parse(response['_body']);
        this.handleErrorOrSuccess('success', 'Guardado exitoso');
        this.modalData = ({ id: id, creationDate: creationDate})
        this.showModal = true;
        this.showView = false;
      })
      .catch(error => {
        this.handleErrorOrSuccess('error', 'Error al guardar');
      });
  }

  private handleErrorOrSuccess(severity: 'success' | 'error', translateKey: string) {
    const message = this.translate.instant(translateKey);
    this.notificationsService.growlNotify(severity, message);
  }

  // Método llamado cuando el tipo de discapacidad cambia
  public onDisabilityChange() {
    const currentDisabilityType = this.form.get('disabilityType').value;
    this.selectedDisability = currentDisabilityType;
    this.updateValidators();

    if (this.previousDisabilityType && this.previousDisabilityType !== currentDisabilityType) {
      // Si el tipo de discapacidad anterior es distinto, borramos el FormGroup correspondiente
      this.removeFormGroup(this.previousDisabilityType);
    }

    // Actualizamos la variable previousDisabilityType con el nuevo valor
    this.previousDisabilityType = currentDisabilityType;
  }

  // Método para eliminar el FormGroup anterior
  private removeFormGroup(tipoDisability: string): void {
    const formGroupKey = tipoDisability;

    // Verificamos si el FormGroup existe y lo eliminamos
    if (this.form.get(formGroupKey)) {
      this.form.removeControl(formGroupKey); // Eliminar el FormGroup correspondiente
    }
  }

  private updateValidators() {
    if (this.selectedDisability === 'work_accident') {
      this.setValidators('arl', true);
      this.setValidators('eps', false);
    } else {
      this.setValidators('eps', true);
      this.setValidators('arl', false);
    }
  }

  private setValidators(controlName: string, required: boolean) {
    const control = this.form.get(controlName);
    if (required) {
      control.setValidators([Validators.required]);
    } else {
      control.clearValidators();
    }
    control.updateValueAndValidity();
  }

  public getDiffDate(date_start: string, date_end: string) {
    const diffDays = getDiffDateDisabilty(date_start, date_end);

    // Establecer el mensaje de alerta y manipular los validadores
    if (diffDays == 1 || diffDays == 2) {
      this.alertMessage = `Resumen De La Atención / Epicrisis ya no es obligatoria ya que la incapacidad tiene una duración de ${diffDays} ${diffDays == 1 ? 'Día' : 'Días'}.`;
      this.updateEpicrisisValidators(false); // Quitar validadores
    } else {
      this.alertMessage = ''; // Limpiar el mensaje
      this.updateEpicrisisValidators(true); // Agregar validadores
    }

    return diffDays;
  }

  // Función para agregar o quitar el validador 'required' del 'epicrisis'
  private updateEpicrisisValidators(isRequired: boolean) {
    const documentsGroup = this.form.get(this.formGroupNameDocuments) as FormGroup;
    if (documentsGroup) {
      const epicrisisArray = documentsGroup.get('epicrisis') as FormArray;
      if (epicrisisArray) {
        if (isRequired) {
          epicrisisArray.setValidators([Validators.required]); // Agregar el validador 'required'
        } else {
          epicrisisArray.clearValidators(); // Quitar el validador 'required'
        }
        epicrisisArray.updateValueAndValidity(); // Actualizar la validez
      }
    }
  }

  public getFormValidationErrors(form: FormGroup): string[] {
    let errors: string[] = [];
    const array_forms = Object.keys(form.controls);

    // Obtener los errores de los controles
    errors = this.getErrorString(array_forms, form);

    // Devolver el primer error que encuentre
    return errors.length ? [errors[0]] : [];
  }

  public getErrorString(array_forms: any, form_local: any): string[] {
    let errors: string[] = [];

    for (let i = 0; i < array_forms.length; i++) {
      const key = array_forms[i];
      const control = form_local.get(key);

      // Si el control es un FormGroup, iteramos sobre sus controles internos
      if (control instanceof FormGroup) {
        // Llamada recursiva a los controles internos del FormGroup
        errors = errors.concat(this.getErrorString(Object.keys(control.controls), control));
      }
      // Si el control tiene errores, lo procesamos
      else if (control.errors) {
        const controlErrors: ValidationErrors = control.errors;
        const keyError = Object.keys(controlErrors)[0];
        let key_with_spaces = this.deleteUnderLine(key);
        let key_translated = '';
        let error_translated = '';

        // Traducción del nombre del campo
        this.translate.get(key_with_spaces).subscribe((res: string) => {
          key_translated = res;
        });

        // Traducción del error
        this.translate.get(keyError).subscribe((res: string) => {
          error_translated = res;
        });

        // Almacenamos el primer error en el arreglo de errores
        if (key_translated && error_translated) {
          errors.push(`Campo: ${key_translated}, Error: ${error_translated}`);
        }
      }
    }

    return errors;
  }

  public deleteUnderLine(chain: string): string {
    return chain.replace(/_/g, ' ');
  }
}



// WEBPACK FOOTER //
// D:/inetpub/wwwroot/kaizen-ng/src/app/modules/disability/components/candidate-disabilities/new-candidate-disabilitie/new-candidate-disabilitie.component.ts